import { CheckCircle2, Mail, Pencil, Trash2, Users } from 'lucide-react';

import type { Announcement } from '@/api/comms/announcements.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { TipTapRenderer } from '@/components/richtext/TipTapRenderer';

/**
 * One announcement in the feed. Unread rows carry a left accent + ring;
 * `requiresAck` rows expose an Acknowledge button until acknowledged.
 * Posters (per `canManage`) additionally get Receipts / Edit / Archive.
 *
 * All side-effects are delegated to the page via callbacks — the card
 * itself is stateless.
 */
export function AnnouncementCard({
  announcement,
  canManage,
  departmentName,
  onMarkRead,
  onAcknowledge,
  onViewReceipts,
  onEdit,
  onArchive,
  actionPending,
}: {
  announcement: Announcement;
  canManage: boolean;
  departmentName?: string;
  onMarkRead: (id: string) => void;
  onAcknowledge: (id: string) => void;
  onViewReceipts: (announcement: Announcement) => void;
  onEdit: (announcement: Announcement) => void;
  onArchive: (announcement: Announcement) => void;
  actionPending?: boolean;
}) {
  const read = announcement.myReceipt?.readAt != null;
  const acknowledged = announcement.myReceipt?.acknowledgedAt != null;
  const needsAck = announcement.requiresAck && !acknowledged;
  const unread = !read;

  return (
    <Card className={`p-5 ${unread ? 'border-l-4 border-l-kw-cerulean ring-1 ring-kw-cerulean/20' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15px] font-semibold text-kw-shark">{announcement.title}</h3>
            {announcement.audience === 'department' ? (
              <Badge variant="neutral">
                <Users className="mr-1 size-3" />
                {departmentName ?? 'Department'}
              </Badge>
            ) : (
              <Badge variant="neutral">Org-wide</Badge>
            )}
            {acknowledged ? (
              <Badge variant="success">Acknowledged</Badge>
            ) : announcement.requiresAck ? (
              <Badge variant="danger">Acknowledgment required</Badge>
            ) : null}
            {announcement.archivedAt ? <Badge variant="warning">Archived</Badge> : null}
          </div>
          <p className="mt-1 text-[12px] text-kw-muted">
            {announcement.authorNameSnapshot} ·{' '}
            {new Date(announcement.publishedAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
            {announcement.emailedAt ? (
              <span className="ml-2 inline-flex items-center gap-1">
                <Mail className="size-3" />
                Emailed
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="mt-3">
        <TipTapRenderer doc={announcement.bodyJson} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {needsAck ? (
          <Button
            size="sm"
            isLoading={actionPending}
            onClick={() => {
              onAcknowledge(announcement.id);
            }}
          >
            Acknowledge
          </Button>
        ) : unread ? (
          <Button
            size="sm"
            variant="secondary"
            isLoading={actionPending}
            onClick={() => {
              onMarkRead(announcement.id);
            }}
          >
            Mark as read
          </Button>
        ) : (
          <span className="inline-flex items-center gap-1 text-[12px] text-kw-muted">
            <CheckCircle2 className="size-3.5 text-kw-tag-text" />
            {acknowledged ? 'Acknowledged' : 'Read'}
          </span>
        )}

        {canManage ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onViewReceipts(announcement);
              }}
            >
              Receipts
            </Button>
            <button
              type="button"
              aria-label={`Edit ${announcement.title}`}
              onClick={() => {
                onEdit(announcement);
              }}
              className="rounded-kw-md p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
            >
              <Pencil className="size-4" />
            </button>
            {!announcement.archivedAt ? (
              <button
                type="button"
                aria-label={`Archive ${announcement.title}`}
                onClick={() => {
                  onArchive(announcement);
                }}
                className="rounded-kw-md p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
              >
                <Trash2 className="size-4" />
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </Card>
  );
}
